import { datadogRum } from '@datadog/browser-rum';
import { FundriseRouteConfig } from 'vue-router';
import { logError } from '@utils/error-tracking';
import { useAppStore } from '@stores/app';
import { useLoginStore } from '@stores/login';
import { validateRouteAndNavigate } from './login-state-guards';

const loginRoutes: Array<FundriseRouteConfig> = [
	{
		path: `/login-verify`,
		name: 'login-two-factor',
		meta: { minTokenScopeRequired: 'REQUIRES_ADDITIONAL_AUTH' },
		beforeEnter: async (to, from, next) => {
			const appStore = useAppStore();
			if (appStore.isAuthenticated) {
				next({ name: 'account-overview' });
			} else if (appStore.tokenScope.includes('REQUIRES_ADDITIONAL_AUTH')) {
				const loginStore = useLoginStore();
				try {
					await loginStore.storeTwoFactorMetadata();
					next();
				} catch {
					datadogRum.addAction('forbidden route', {
						from: from.name,
						to: to.name,
						scope: appStore.tokenScope
					});
					next({ name: 'forbidden' });
				}
			} else {
				datadogRum.addAction('forbidden route', { from: from.name, to: to.name, scope: appStore.tokenScope });
				next({ name: 'forbidden' });
			}
		},
		component: () => import('@views/account/login/login-two-factor.vue'),
		redirect: { name: 'login-two-factor-select' },
		children: [
			{
				path: `select`,
				name: 'login-two-factor-select',
				meta: { minTokenScopeRequired: 'REQUIRES_ADDITIONAL_AUTH' },
				beforeEnter: (to) => {
					const loginStore = useLoginStore();
					loginStore.resetTwoFactorSelections();
					return validateRouteAndNavigate(to);
				},
				component: () => import('@views/account/login/login-two-factor-select.vue')
			},
			{
				path: `confirm`,
				name: 'login-two-factor-confirm',
				meta: { minTokenScopeRequired: 'REQUIRES_ADDITIONAL_AUTH' },
				beforeEnter: (to) => {
					return validateRouteAndNavigate(to);
				},
				component: () => import('@views/account/login/login-two-factor-confirm.vue')
			}
		]
	},
	{
		path: '/login-verify/link-google',
		name: 'login-verify-link-google',
		meta: { minTokenScopeRequired: 'GOOGLE_AUTH_SETUP', suppressDefaultMxpViewEvent: true },
		props: { type: 'google' },
		component: () => import('@views/account/login/login-verify-link-google-apple.vue')
	},
	{
		path: '/login-verify/link-apple',
		name: 'login-verify-link-apple',
		meta: { minTokenScopeRequired: 'APPLE_AUTH_SETUP', suppressDefaultMxpViewEvent: true },
		props: { type: 'apple' },
		component: () => import('@views/account/login/login-verify-link-google-apple.vue')
	},
	{
		path: `/login`,
		name: 'login',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED', suppressDefaultMxpViewEvent: true },
		beforeEnter: async (to, from, next) => {
			const appStore = useAppStore();
			const isMaintenanceWindow = await appStore.isFeatureEnabled('app-down-maintenance-window');
			if (isMaintenanceWindow) {
				next({ name: 'maintenance' });
			} else {
				try {
					if (appStore.isAuthenticated || appStore.oauthToken || appStore.tokenScope !== 'UNAUTHENTICATED') {
						await appStore.revokeUserToken();
					}
				} catch {}

				next();
			}
		},
		component: () => import('@views/login/login-view.vue')
	},
	{
		path: `/logout-success`,
		name: 'logout-success',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/logout/logout-success.vue'),
		beforeEnter: async () => {
			try {
				const appStore = useAppStore();
				if (appStore.isAuthenticated || appStore.oauthToken || appStore.tokenScope !== 'UNAUTHENTICATED') {
					await appStore.revokeUserToken();
				}
			} catch {}
		}
	},
	{
		path: `/locked-out`,
		name: 'login-locked-out',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		component: () => import('@views/account/login/login-locked-out.vue')
	},
	{
		path: '/customer-service/substitute-user/:userId/make-the-switch',
		name: 'substitute-user',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			const appStore = useAppStore();
			if (appStore.isAuthenticated || appStore.oauthToken || appStore.tokenScope !== 'UNAUTHENTICATED') {
				await appStore.revokeUserToken();
			}

			try {
				await appStore.authenticateFromCornice();
				next({ name: 'home' });
			} catch (e) {
				if (e instanceof Error || typeof e === 'string') {
					logError(e);
				}
				datadogRum.addAction('forbidden route', { from: from.name, to: to.name, scope: appStore.tokenScope });
				next({ name: 'forbidden' });
			}
		},
		// revisit using redirect
		component: () => import('@views/login/login-view.vue')
	}
];
export default loginRoutes;
